import React, { useState, useEffect } from "react";
import { Col, Collapse, Form, Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import {
  FormGroupLabel,
  FormItem,
  HintText,
} from "../../shared/components/form/FormElements";
import FormField from "../../shared/components/form/FormField";
import { emailPattern } from "../../shared/helpers";
import { useTranslation } from "react-i18next";
import { formInputStyles } from "../../shared/constants/styles";
import { colorLightGray } from "../../utils/palette";
import styled from "styled-components";
import {
  AddFormButton,
  FilterButton,
} from "../../shared/components/TableElements";
import Error from "@/shared/components/form/Error";
import PasswordField from "../../shared/components/form/PasswordField";

const UpdateCredentials = ({
  currentEmail,
  credentialsOpen,
  setCredentialsOpen,
  selfUpdate,
}) => {
  const { t } = useTranslation("common");
  const {
    handleSubmit,
    reset,
    register,
    control,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const [showChangeEmail, setShowChangeEmail] = useState(false);

  const newEmail = watch("newEmail");
  const newPassword = watch("newPassword");
  const confirmPassword = watch("confirmPassword");

  // Auto-open Login Credentials section when there are validation errors
  useEffect(() => {
    if (errors?.newEmail || errors?.newPassword || errors?.confirmPassword) {
      if (!credentialsOpen) {
        setCredentialsOpen(true);
      }
    }
  }, [
    errors?.newEmail,
    errors?.newPassword,
    errors?.confirmPassword,
    credentialsOpen,
    setCredentialsOpen,
  ]);

  return (
    <div>
      {/* Header */}
      <CollapseHeader
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: "pointer" }}
        onClick={() => setCredentialsOpen(!credentialsOpen)}
      >
        <div className="d-flex align-items-center gap-2">
          <i
            className="fi fi-rr-shield"
            style={{
              position: "relative",
              top: "1px",
              fontSize: 12,
              fontWeight: 500,
            }}
          ></i>
          <p style={{ fontSize: 13, fontWeight: 300 }} className="mb-0">
            {t("login_credentials")}
          </p>
        </div>
        <i
          className={`fi fi-rr-angle-small-${
            credentialsOpen ? "down" : "right"
          }`}
          style={{ position: "relative", top: "1px" }}
        ></i>
      </CollapseHeader>

      {/* Collapsible Content */}
      <CollapseContainer in={credentialsOpen}>
        <div>
          {selfUpdate && (
            <HintText className="mb-2">{t("self_cred_update_msg")}</HintText>
          )}
          {showChangeEmail ? (
            <div className="d-flex flex-column gap-2">
              <div>
                <FormItem id="update-email-input-div">
                  <FormField
                    name="newEmail"
                    id="update-email-input"
                    control={control}
                    component="input"
                    defaultValue=""
                    placeholder={t("enter_new_email")}
                    autoComplete="off"
                    rules={{
                      required: t("required_err"),
                      pattern: {
                        value: emailPattern,
                        message: t("invalid_email_err_msg"),
                      },
                    }}
                    style={formInputStyles}
                    //disabled={type === "edit"}
                    //onKeyUp={handleSIOdoChange}
                  />
                </FormItem>

                {errors?.newEmail && <Error error={errors.newEmail.message} />}
              </div>
              <div className="d-flex">
                <FilterButton
                  type="button"
                  onClick={() => {
                    setShowChangeEmail(false);
                    setValue("newEmail", currentEmail);
                    clearErrors("newEmail");
                  }}
                  style={{
                    height: "1.5rem",
                    fontSize: 12,
                    fontWeight: 300,
                    position: "relative",
                    top: "1px",
                  }}
                >
                  {t("cancel")}
                </FilterButton>
              </div>
              <HintText>{t("confirm_email_change_msg")}</HintText>
            </div>
          ) : (
            <div className="d-flex gap-2 align-items-center">
              <p>{newEmail}</p>

              <FilterButton
                type="button"
                onClick={() => {
                  setValue("newEmail", currentEmail);
                  setShowChangeEmail(true);
                }}
                style={{
                  height: "1.5rem",
                  fontSize: 12,
                  fontWeight: 300,
                  position: "relative",
                  top: "1px",
                }}
              >
                {t("change_email")}
              </FilterButton>
            </div>
          )}

          <hr className="mb-0" />
          {/* Password */}
          <Row>
            <Col md={6}>
              <FormGroupLabel id="password-label">
                {t("new_password")}
              </FormGroupLabel>
              <FormItem disabled={false} id="password-div">
                <PasswordField
                  name="newPassword"
                  id="password"
                  control={control}
                  placeholder={t("new_password_placeholder")}
                  autoComplete="new-password"
                  rules={{
                    //required: t("required_err"),
                    minLength: {
                      value: 6,
                      message: t("invalid_pwd"),
                    },
                    // validate: {
                    //   changePassword: (v) =>
                    //     v !== newPassword || t("change_pw_msg"),
                    // },
                  }}
                />
              </FormItem>

              {errors?.newPassword && (
                <Error error={errors.newPassword.message} />
              )}
            </Col>
            <Col md={6}>
              <FormGroupLabel id="confirm-password-label">
                {t("confirm_password")}
              </FormGroupLabel>
              <FormItem disabled={false} id="confirm-password-div">
                <PasswordField
                  name="confirmPassword"
                  id="confirm-password"
                  control={control}
                  placeholder={t("confirm_password")}
                  autoComplete="new-password"
                  rules={{
                    //required: t("required_err"),
                    minLength: {
                      value: 6,
                      message: t("invalid_pwd"),
                    },
                    validate: {
                      matchPassword: (v) =>
                        v === newPassword || t("pw_mismatch_err_msg"),
                    },
                  }}
                />
              </FormItem>

              {errors?.confirmPassword && (
                <Error error={errors.confirmPassword.message} />
              )}
            </Col>
          </Row>
          <HintText className="mt-2">{t("password_change_email_msg")}</HintText>
        </div>
      </CollapseContainer>
    </div>
  );
};

export default UpdateCredentials;

//styles
const CollapseContainer = styled(Collapse)`
  border: 1px solid ${colorLightGray};
  border-radius: 5px;
  padding: 1rem;
  border-top: none;
`;

const CollapseHeader = styled.div`
  border: 1px solid ${colorLightGray};
  border-radius: 5px;
  padding: 0.45rem;

  &:hover {
    background: ${colorLightGray};
  }
`;
